import { useEffect, useMemo, useRef } from 'react';

import { cn } from '~/utils/cn';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';
import type { ContractInstallment } from '~/store/contracts/interfaces';

import { Text } from '../../atoms/Text/view';

interface PropsContractTimeline {
  installments: ContractInstallment[];
  activeId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

const MONTHS = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
const fmtDay = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`;
};

const DAY = 24 * 60 * 60 * 1000;
const dayIdx = (iso: string) => {
  const d = new Date(iso);
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / DAY);
};

type DerivedStatus = 'paid' | 'overdue' | 'next' | 'future';

const STATUS_STYLE: Record<
  DerivedStatus,
  { dot: string; ring: string; line: string; label: string; labelColor: string }
> = {
  paid: {
    dot: 'bg-success-04',
    ring: 'border-success-04',
    line: 'bg-success-04',
    label: 'Pago',
    labelColor: 'color-success-06',
  },
  overdue: {
    dot: 'bg-warning-04',
    ring: 'border-warning-04',
    line: 'bg-intense-03',
    label: 'Vencida',
    labelColor: 'color-warning-06',
  },
  next: {
    dot: 'bg-primary-01',
    ring: 'border-primary-01',
    line: 'bg-intense-03',
    label: 'Próxima',
    labelColor: 'color-primary-01',
  },
  future: {
    dot: 'bg-intense-04',
    ring: 'border-intense-04',
    line: 'bg-intense-03',
    label: 'Futura',
    labelColor: 'color-intense-06',
  },
};

export default function ContractTimeline({
  installments,
  activeId,
  onSelect,
  className,
}: PropsContractTimeline) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const sorted = useMemo(
    () =>
      [...installments].sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      ),
    [installments],
  );

  const derived: { item: ContractInstallment; status: DerivedStatus }[] = useMemo(() => {
    const todayIdx = dayIdx(new Date().toISOString());
    const firstUnpaidIndex = sorted.findIndex((i) => i.status !== 'paid');
    return sorted.map((item, idx) => {
      if (item.status === 'paid') return { item, status: 'paid' as const };
      const diff = dayIdx(item.dueDate) - todayIdx;
      if (diff < 0) return { item, status: 'overdue' as const };
      if (idx === firstUnpaidIndex) return { item, status: 'next' as const };
      return { item, status: 'future' as const };
    });
  }, [sorted]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLDivElement>(
      activeId ? `[data-id="${activeId}"]` : '[data-active="true"]',
    );
    if (target) {
      const left = target.offsetLeft - container.clientWidth / 2 + target.clientWidth / 2;
      container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }, [activeId, derived.length]);

  if (sorted.length === 0) {
    return (
      <Text className="text-bodyMD color-intense-06">Sem parcelas cadastradas.</Text>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <div
        ref={containerRef}
        className="overflow-x-auto scrollbar-thin"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="flex items-stretch gap-0 px-quark py-small min-w-max">
          {derived.map(({ item, status }, idx) => {
            const style = STATUS_STYLE[status];
            const isActive = activeId
              ? item.id === activeId
              : status === 'next' || status === 'overdue';
            const isLast = idx === derived.length - 1;
            return (
              <div
                key={item.id}
                data-id={item.id}
                data-active={isActive}
                className="flex flex-col items-center min-w-[88px] cursor-pointer group"
                onClick={() => onSelect?.(item.id)}
              >
                <Text className="text-captionMD color-intense-06">
                  {fmtDay(item.dueDate)}
                </Text>
                <div className={`
                relative w-full flex items-center justify-center my-quark
                ${isActive ? 'mt-[1px]' : ''}
                `}>
                  <div
                    className={cn(
                      'absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px]',
                      style.line,
                      idx === 0 && 'left-1/2',
                      isLast && 'right-1/2',
                    )}
                  />
                  <span
                    className={cn(
                      'relative z-10 h-[16px] w-[16px] rounded-full border-2 bg-intense-01 transition-transform',
                      style.ring,
                      isActive && 'h-[22px] w-[22px] shadow-level1',
                      'group-hover:scale-110',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute inset-[2px] rounded-full',
                        style.dot,
                      )}
                    />
                  </span>
                </div>
                <Text className="text-captionLG color-complement-01">
                  {`#${item.index}`}
                </Text>
                <Text className="mt-quark text-caption color-intense-06">
                  {formatCentsToBRLCompact(item.amount)}
                </Text>
                <Text className={cn('mt-quark text-caption', style.labelColor)}>
                  {style.label}
                </Text>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
